import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Paper,
  Slide,
  useTheme,
  useMediaQuery,
  Fab
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  NotificationsActive as NotificationsActiveIcon,
  Smartphone as SmartphoneIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import notificationService from '../services/notificationService';
import { config } from '../config/environment';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface NotificationPermissionDialogProps {
  open: boolean;
  onClose: () => void;
  onPermissionGranted: () => void;
  onPermissionDenied: () => void;
}

const NotificationPermissionDialog: React.FC<NotificationPermissionDialogProps> = ({
  open,
  onClose,
  onPermissionGranted,
  onPermissionDenied
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isRequesting, setIsRequesting] = useState(false);

  const handleAllowNotifications = async () => {
    setIsRequesting(true);
    
    try {
      const permission = await notificationService.requestPermission();
      
      if (permission === 'granted') {
        // Enviar notificação de teste
        await notificationService.sendTestNotification();
        onPermissionGranted();
      } else {
        onPermissionDenied();
      }
    } catch (error) {
      console.error('Erro ao solicitar permissão:', error);
      onPermissionDenied();
    } finally {
      setIsRequesting(false);
      onClose();
    }
  };

  const handleDeny = () => {
    onPermissionDenied();
    onClose();
  };

  const benefits = [
    {
      icon: <MessageIcon sx={{ color: '#25D366' }} />,
      title: 'Novas Mensagens',
      description: 'Receba notificações instantâneas quando chegarem novas mensagens no WhatsApp, Telegram ou Instagram.'
    },
    {
      icon: <NotificationsActiveIcon sx={{ color: '#1976d2' }} />,
      title: 'Atualizações Importantes',
      description: 'Mantenha-se informado sobre mudanças no sistema, novos leads e atualizações de campanhas.'
    },
    {
      icon: <SmartphoneIcon sx={{ color: '#ff9800' }} />,
      title: 'Acesso Rápido',
      description: 'Toque na notificação para ir diretamente para a conversa ou funcionalidade relacionada.'
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: isMobile ? 0 : 3,
          m: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100%' : '90vh'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ 
        textAlign: 'center', 
        pb: 1,
        position: 'relative',
        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
        color: 'white'
      }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white'
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2 }}>
          <Fab
            sx={{
              bgcolor: 'white',
              color: '#1976d2',
              mb: 2,
              '&:hover': {
                bgcolor: '#f5f5f5'
              }
            }}
            size="large"
          >
            <NotificationsIcon sx={{ fontSize: 32 }} />
          </Fab>
          
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
            Ativar Notificações
          </Typography>
          
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            Mantenha-se sempre conectado
          </Typography>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ px: 3, py: 4 }}>
        <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
          Permita que o <strong>{config.app.platformName || 'Klinvia'}</strong> envie notificações para você não perder nenhuma mensagem importante.
        </Typography>

        {/* Benefits */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {benefits.map((benefit, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: theme.shadows[2]
                }
              }}
            >
              <Box sx={{
                minWidth: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {benefit.icon}
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {benefit.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {benefit.description}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        {/* Privacy Note */}
        <Box sx={{
          mt: 3,
          p: 2,
          bgcolor: 'grey.50',
          borderRadius: 2,
          border: 1,
          borderColor: 'grey.200'
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
            🔒 <strong>Sua privacidade é importante:</strong> Você pode desativar as notificações a qualquer momento nas configurações do seu navegador ou dispositivo.
          </Typography>
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ p: 3, gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
        <Button
          onClick={handleDeny}
          variant="outlined"
          size="large"
          fullWidth={isMobile}
          sx={{
            borderColor: 'grey.300',
            color: 'text.secondary',
            '&:hover': {
              borderColor: 'grey.400',
              bgcolor: 'grey.50'
            }
          }}
        >
          Agora Não
        </Button>
        
        <Button
          onClick={handleAllowNotifications}
          variant="contained"
          size="large"
          fullWidth={isMobile}
          disabled={isRequesting}
          startIcon={<NotificationsActiveIcon />}
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            boxShadow: theme.shadows[4],
            '&:hover': {
              background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
              boxShadow: theme.shadows[6]
            }
          }}
        >
          {isRequesting ? 'Ativando...' : 'Ativar Notificações'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationPermissionDialog;



